import { useCallback, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Button, IconButton } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  ConditionalTemplateAnswer_FieldsFragment,
  ConditionalTemplateAnswer_GetConditionalTemplateAnswersForConditionalTemplateAnswerDataGridDocument,
  useConditionalTemplateAnswer_DeleteConditionalTemplateAnswerByIdMutation,
} from 'queries';
import { Icon } from 'ts-react-feather-icons';

import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

const useStyles = makeStyles(() => ({
  actionButton: {
    cursor: 'pointer',
  },
  conditionalTemplateAnswerActionButton: {
    color: '#1C2541',
  },
  menu: {
    borderRadius: '8px',
    border: '1px solid #ECEEF1',
    boxShadow: ' 0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
    backgroundColor: '#fff',
  },
  menuItem: {
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  redButtonColor: {
    color: '#9E2B25',
  },
}));

type Props = {
  conditionalTemplateAnswer: ConditionalTemplateAnswer_FieldsFragment;
  onEditSelectedConditionalTemplateAnswer: Function;
};

const ConditionalTemplateAnswerActions = ({
  conditionalTemplateAnswer,
  onEditSelectedConditionalTemplateAnswer,
}: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [deleteConditionalTemplateAnswer] =
    useConditionalTemplateAnswer_DeleteConditionalTemplateAnswerByIdMutation({
      refetchQueries: [
        {
          query:
            ConditionalTemplateAnswer_GetConditionalTemplateAnswersForConditionalTemplateAnswerDataGridDocument,
          variables: {
            input: {
              questionId: conditionalTemplateAnswer.conditionalTemplateQuestionId,
              paginationInput: {
                page: defaultPaginationModel.page,
                pageSize: defaultPaginationModel.pageSize,
              },
            },
          },
        },
      ],
      onCompleted: () => {
        showToast({
          message: 'Selected answer has been deleted.',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Unable to delete selected answer' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const editConditionalTemplateAnswer = () => {
    onEditSelectedConditionalTemplateAnswer();
  };

  const handleDeleteConditionalTemplateAnswer = () => {
    if (!window.confirm('Are you sure you want to delete this answer?')) {
      return;
    }
    deleteConditionalTemplateAnswer({
      variables: {
        id: conditionalTemplateAnswer.id,
      },
    });
  };

  const getMenuItems = useCallback(() => {
    const menuItems = [
      {
        key: 'edit',
        label: 'Edit',
        icon: <Icon name="edit-2" size={16} color="#44D2F2" />,
        onClick: () => {
          editConditionalTemplateAnswer();
        },
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <DeleteIcon
            className={clsx(classes.actionButton, classes.redButtonColor)}
            fontSize="small"
          />
        ),
        onClick: () => {
          handleDeleteConditionalTemplateAnswer();
        },
      },
    ];

    return menuItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionalTemplateAnswer]);

  return (
    <>
      <IconButton
        id="action-button"
        aria-controls={open ? 'action-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        PaperProps={{
          elevation: 0,
          className: classes.menu,
        }}
        id="action-menu"
        MenuListProps={{
          'aria-labelledby': 'action-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {getMenuItems().map((item) => (
          <MenuItem key={item.key} disableRipple className={classes.menuItem}>
            <Button
              className={classes.conditionalTemplateAnswerActionButton}
              size="small"
              startIcon={item.icon}
              onClick={item.onClick}
            >
              {item.label}
            </Button>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ConditionalTemplateAnswerActions;
